import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";

/**
 * STX-scoped Theme.
 * Der Umschalter wird global gespeichert (localStorage), wirkt aber nur
 * innerhalb <STXThemeScope> — d.h. ausschließlich für stilltouchedX-Seiten.
 */
const LS_KEY = "stx-theme-mode";
// iter162 · Migrations-Marker: setzt alten „light"-Default einmalig auf dark zurück, damit
// Bestandsuser (die vor iter162 hell gestartet sind, ohne aktiv umgeschaltet zu haben)
// jetzt beim nächsten Öffnen wieder auf Dark landen. Wer bewusst auf Light umgeschaltet
// hat, kann nach dem Migrationslauf jederzeit erneut wechseln.
const MIGRATION_KEY = "stx-theme-mode.iter162-dark-default";

const STXThemeContext = createContext({
  mode: "dark",
  setMode: () => {},
  toggle: () => {},
});

export function useSTXTheme() {
  return useContext(STXThemeContext);
}

export function STXThemeProvider({ children }) {
  const [mode, setModeState] = useState(() => {
    try {
      const migrated = localStorage.getItem(MIGRATION_KEY);
      if (!migrated) {
        // Einmalige Rücksetzung auf Dark; danach zählt die Nutzer-Entscheidung wieder.
        localStorage.setItem(LS_KEY, "dark");
        localStorage.setItem(MIGRATION_KEY, "1");
        return "dark";
      }
      const stored = localStorage.getItem(LS_KEY);
      if (stored === "light" || stored === "dark") return stored;
    } catch { /* noop */ }
    return "dark";
  });

  const setMode = useCallback((next) => {
    setModeState(next);
    try { localStorage.setItem(LS_KEY, next); } catch { /* noop */ }
  }, []);

  const toggle = useCallback(() => {
    setModeState((prev) => {
      const next = prev === "dark" ? "light" : "dark";
      try { localStorage.setItem(LS_KEY, next); } catch { /* noop */ }
      return next;
    });
  }, []);

  const value = useMemo(() => ({ mode, setMode, toggle }), [mode, setMode, toggle]);

  return <STXThemeContext.Provider value={value}>{children}</STXThemeContext.Provider>;
}

/**
 * Wickelt STX-Seiten in einen Bereich, in dem CSS-Variablen für die STX-Palette gelten.
 * Wenn mode="light", wird zusätzlich die Klasse "stx-light" gesetzt.
 */
export function STXThemeScope({ children, className = "" }) {
  const { mode } = useSTXTheme();
  return (
    <div
      data-testid="stx-theme-scope"
      data-stx-mode={mode}
      className={`stx-theme ${mode === "light" ? "stx-light" : ""} min-h-full flex-1 flex flex-col ${className}`}
      style={{ minHeight: "100%" }}
    >
      {children}
    </div>
  );
}
